// -----------------------------------------------------------------------------
// whatsappSendCompletion — the "which task does a WhatsApp send complete?" rules.
//
// Extracted so the decision logic is unit-testable without mounting FollowUpV2
// (2700 lines) or ScheduleReminderTaskSection (1200 lines). Both import from here;
// neither keeps a second copy of the rules.
//
// Background — RCA_2026-10-13_whatsapp_step_never_completes.md:
// sending a WhatsApp from the sidebar composer completed nothing, because the
// confirmation card's "Mark message sent" button was the only completion path and
// it sits behind the composer the rep is typing in. The anchor below restores the
// behaviour that existed until 2026-06-07 (commit 8ee05f441), using the same
// set-on-open / clear-on-close / consume-on-use shape as NewLead.jsx:1513-1522.
// -----------------------------------------------------------------------------
import { ITEM_TYPE_TO_CHANNEL } from './standaloneTaskConstants';

/**
 * Decide whether a checklist item / standalone task should arm the send-anchor.
 *
 * Returns a context object for WhatsApp-channel items that carry a task id, and
 * null for everything else. Null is meaningful: callers ALWAYS set-or-clear, so
 * opening a non-WhatsApp task wipes a previous WhatsApp task's anchor rather than
 * leaving it armed (which would complete the wrong step on the next send).
 *
 * NOTE: read item_type from the ORIGINAL item. ScheduleReminderTaskSection
 * rewrites standalone tasks to LEAD_NEXT_ACTION before handing them to the panel,
 * so resolving the channel after that rewrite would always miss.
 */
export function resolveWhatsappArmContext(item) {
    if (!item || !item.taskId) return null;
    if (ITEM_TYPE_TO_CHANNEL[item.item_type] !== 'whatsapp') return null;
    return {
        taskId: item.taskId,
        itemId: item.itemId || null,
        isStandaloneTask: Boolean(item.isStandaloneTask),
        itemType: item.item_type,
    };
}

/**
 * Translate an armed context into the completion call to make.
 *
 *   { kind: 'task' }  → flat standalone cadence step ("24 hr WhatsApp") →
 *                       completeTask(practiceId, taskId)
 *   { kind: 'item' }  → checklist-backed item → completeChecklistItem(...)
 *   { kind: 'none' }  → nothing armed, or armed without the ids the thunk needs.
 *
 * Mirrors the branch in ScheduleReminderTaskSection.handlePanelComplete so the two
 * completion routes can never disagree about which thunk a given item needs.
 */
export function planWhatsappCompletion(ctx) {
    if (!ctx || !ctx.taskId) return { kind: 'none' };
    if (ctx.isStandaloneTask) return { kind: 'task', taskId: ctx.taskId };
    if (!ctx.itemId) return { kind: 'none' };
    return { kind: 'item', taskId: ctx.taskId, itemId: ctx.itemId };
}

/**
 * The anchor itself: remembers which task opened the WhatsApp composer.
 *
 * Lifecycle, matching NewLead.jsx's pendingV2TaskIdRef:
 *   arm(item)  — on action-panel open. Set-or-clear: a non-WhatsApp item disarms.
 *   disarm()   — on panel close, on completion by another route, and on modal close.
 *   consume()  — on a CONFIRMED send. One-shot: returns the context and clears it,
 *                so three messages sent against one open task complete it once.
 */
export function createWhatsappTaskAnchor() {
    let ctx = null;
    return {
        arm(item) {
            ctx = resolveWhatsappArmContext(item);
            return ctx;
        },
        disarm() {
            ctx = null;
        },
        consume() {
            const current = ctx;
            ctx = null;
            return current;
        },
        peek() {
            return ctx;
        },
    };
}
